import React, { useContext, useEffect, useState } from 'react'; import { useLocation, useHistory } from 'react-router-dom';
import { Col, Grid, Row } from 'react-bootstrap';
import qs from 'qs';
import { useDebounce } from 'use-debounce';
import { isEmpty } from 'lodash';
import Table from './components/Table'
import NavRoutes from '../../../../components/Navs/NavRoutes';
import { AppearanceContext } from '../../index';
import { getErrorMessage, pageWrapperScrollTop } from '../../../../utils/helper';
import AlertComponent from '../../../../components/Alert';
import PageNotFound from '../../../404';
import { filesDelete, filesList, filesListPagination, filesUpload } from '../../../../services/endpoints/files';
import SearchInput from '../../../../components/Search/SearchInput';
import Spinner from '../../../../components/Loaders/Spinner';
import Pagination from '../../../../components/Pagination';
import { pickDevParams } from '../../../../utils/config';
import FileButton from '../../../../components/FileButton';
import ModalsManager from './components/Modals/Manager';
import Loader from '../../../../components/Loaders/Loader';

const Files = () => {
  const location = useLocation();
  const history = useHistory();

  const { config, menu } = useContext(AppearanceContext);

  const queryString = qs.parse(location.search.slice(1));

  const [init, setInit] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null)
  const [tableError, setTableError] = useState(null)
  const [isLoading, setIsLoading] = useState({ page: true, pagination: true, table: false, uploading: false, modalSubmit: false });

  const [openModal, setOpenModal] = useState('');
  const [modalData, setModalData] = useState({});

  const [searchQuery, setSearchQuery] = useState(queryString.query || '');
  const [searchDebounce] = useDebounce(searchQuery, 350);

  const { files } = data;

  const fetchList = async () => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));
    setTableError(null);
    try {
      const response = await filesList(queryString);
      if (response.success) {
        setData(response.data);
      }
      setIsLoading((prevState) => ({ ...prevState, table: false }));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  };

  const fetchListPagination = async () => {
    setIsLoading((prevState) => ({ ...prevState, pagination: true }));

    try {
      const response = await filesListPagination(queryString);
      if (response.success) {
        setData((prevState) => ({ ...prevState, pagination: response.data.pagination }));
      }
      setIsLoading((prevState) => ({ ...prevState, pagination: false }));
      return response.data;
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, pagination: false }));
      return error;
    }
  };

  const onUploadFile = async (e) => {
    const formData = new FormData();
    for (let i = 0; i < e.target.files.length; i++) {
      formData.append('files[]', e.target.files[i]);
    }
    Object.keys(pickDevParams).forEach((param) => {
      const value = pickDevParams[param];
      formData.append(param, value);
    })
    setIsLoading((prevState) => ({ ...prevState, uploading: true }));
    try {
      await filesUpload(formData)
      isEmpty(queryString) ? fetchList() : history.push(location.pathname);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setTableError(errorMessage);
    } finally {
      setIsLoading((prevState) => ({ ...prevState, uploading: false }));
    }
  }

  useEffect(() => {
    if (!init || (queryString.query === undefined && searchDebounce === '')) {
      return;
    }

    const newQuery = queryString ? { ...queryString } : {};
    if (searchDebounce) {
      newQuery.query = searchDebounce;
    } else {
      delete newQuery.query;
    }

    delete newQuery.page;

    if (queryString.query !== searchDebounce) {
      history.push({
        pathname: location.pathname,
        search: qs.stringify(newQuery),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounce])

  const getAllData = async () => {
    setTableError(null)
    setIsLoading((prevState) => ({ ...prevState, page: true }));
    try {
      if (config?.access?.files_list) {
        const listResponse = await fetchList(queryString);
        setIsLoading((prevState) => ({ ...prevState, page: false }));
        if (listResponse?.need_pagination) {
          await fetchListPagination();
        } else {
          setIsLoading((prevState) => ({ ...prevState, pagination: false }));
        }
      } else {
        setIsLoading((prevState) => ({ ...prevState, page: false }));
      }
      setInit(true);
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  }

  const onHideModals = () => {
    setOpenModal('');
    setModalData({});
  };

  const onDeleteFileConfirm = async (fileId) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await filesDelete(fileId);
      onHideModals('');
      fetchList();
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  }

  const onSubmitModal = async () => {
    switch (modalData?.actionType) {
    case 'single':
      if (modalData?.title === 'Delete file') {
        await onDeleteFileConfirm(modalData.item?.id)
      }
      break;

    default:
      break;
    }
  };

  const onDeleteFile = (e, file) => {
    e.preventDefault();
    setModalData({
      actionKey: 'delete',
      title: 'Delete file',
      description: '',
      actionType: 'single',
      item: file,
    });
    setOpenModal('confirm');
  }

  useEffect(() => {
    if (queryString?.page) {
      pageWrapperScrollTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString.page]);

  useEffect(() => {
    getAllData()
    document.title = 'Files';

    const queryValue = queryString.query || '';
    if (queryValue !== searchQuery) {
      setSearchQuery(queryValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (isLoading.page && !init) {
    return <Loader />;
  }

  if (!config?.access?.files_list) {
    return <PageNotFound />
  }

  return (
    <Grid>
      <ModalsManager
        open={openModal}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        modalData={modalData}
        values={[]}
        statuses={config?.statuses}
        onSubmit={onSubmitModal}
        onHide={onHideModals}
      />
      <Row>
        <Col md={2} mdOffset={1}>
          <NavRoutes
            routes={menu}
          />
        </Col>
        <Col md={8}>
          {error ? <AlertComponent text={error} />
            : (
              <>
                <div className="files-table__header">
                  <div>
                    {config?.access?.files_upload
                      ? (
                        <FileButton
                          multiple
                          onChange={onUploadFile}
                          isLoading={isLoading.uploading}
                          accept={['png', 'jfif', 'jpe', 'jpeg', 'jpg', 'gif', 'webp', 'svg', 'svgz', 'eot', 'ttf', 'woff2', 'woff', 'otf', 'js', 'css']}
                          name="Upload files"
                        />
                      ) : null }
                  </div>
                  <div>
                    <SearchInput
                      placeholder="Search"
                      onChange={(e) => setSearchQuery(e.target.value)}
                      value={searchQuery}
                    />
                  </div>
                </div>
                <div className="loader-container" style={{ minHeight: '320px' }}>
                  {tableError ? <AlertComponent text={tableError} /> : null}
                  <div className="m-b">
                    <Table
                      items={files || []}
                      config={config}
                      isLoading={isLoading.table}
                      onDeleteFile={onDeleteFile}
                    />
                  </div>
                </div>
                {config?.access?.files_list_pagination ? (
                  <>
                    {isLoading.pagination || isLoading.table ? (
                      <div className="pagination">
                        <Spinner />
                      </div>
                    ) : (
                      <Pagination
                        hideCountText
                        pages={data.pagination?.pages}
                        currentPage={data.pagination?.current_page}
                        itemsCount={data.pagination?.total_count}
                        pageSize={data.pagination?.page_size}
                        pagesLimit={data.pagination?.pages < 10 ? data.pagination?.pages : 10}
                      />
                    )}
                  </>
                ) : null }
              </>
            )}
        </Col>
      </Row>
    </Grid>
  );
};

export default Files;
